import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    AdyenRegisterCardRequest,
    CartEvents,
    CartException,
    CartExceptionType,
    ComponentPaymentMethodName,
    PayPalPaymentDataRequest,
    PayloadPayPal,
    PaymentCart,
    PaymentMethods,
    PaymentMethodsType,
    PaymentRequest,
    PaymentResponse,
    PaymentToken,
    ProcessorName,
    SaleStatus,
} from '@cargos/sprintpay-models';
import { CartService, PaymentService } from '@cargos/sprintpay-services';
import { AddressFormType, AdvancedFlowApiService, TokenizedCardPayload } from '@cargos/sprintpay-ui';
import {
    Observable,
    Subject,
    catchError,
    combineLatest,
    defaultIfEmpty,
    forkJoin,
    map,
    mergeMap,
    of,
    switchMap,
    take,
    throwError,
} from 'rxjs';
import { ChoiceInformationService } from 'src/app/modules/choice/services';
import { CreditCardForm } from '../components/form-guest-credit-card/models/credit-card-form';
import { PersonalFormType } from '../components/form-personal/models/personal-form';
import { PaymentStatusType } from '../models/payment-status';
import { NotesForm } from '../modules/cart/lib-cart/balance/notes/models/notes-form';
import { PaymentMethodGuest } from '../modules/cart/lib-cart/shared/models/payment-method.model';
import { PaymentMethodsHandlerService } from '../modules/cart/lib-cart/shared/services/payment-methods-handler.service';
import { InformationCollectorByPaymentService } from '../modules/information-collector-by-payment/collector/services/information-collector-by-payment.service';
import { CartHandlerService } from './cart-handler.service';
import { CartBillService } from './cart.service';
import { ConfigService } from './config.service';
import { CustomerService } from './customer.service';
import { DynamicsFieldsService } from './dynamic-field.service';
import { FeatureRestrictionsService } from './features/feature-restrictions.service';
import { PaymentConfirmationService } from './payment-confirmation.service';
import { PayPalService } from './paypal.service';
import { PromoCodesService } from './promo-code.service';
import { SessionService } from './session.service';
import { TermsOfUseForm } from './summary/models/types';
import { SummaryService } from './summary/summary.service';

export interface PaymentValidation {
    isValid: boolean;
    exception?: CartException;
}

@Injectable({
    providedIn: 'root',
})
export class PaymentCartService {
    private _submitAccures: Subject<boolean> = new Subject<boolean>();

    constructor(
        private sessionService: SessionService,
        private paymentService: PaymentService,
        private summaryService: SummaryService,
        private router: Router,
        private cartBillService: CartBillService,
        private paypalService: PayPalService,
        private paymentConfirmationService: PaymentConfirmationService,
        private choiceInformationService: ChoiceInformationService,
        private configService: ConfigService,
        private customerService: CustomerService,
        private cartService: CartService,
        private promoCodeService: PromoCodesService,
        private featureRestrictionsService: FeatureRestrictionsService,
        private informationCollectorByPaymentService: InformationCollectorByPaymentService,
        private dynamicsFieldsService: DynamicsFieldsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService,
        private cartHandlerService: CartHandlerService,
        private advancedFlowApiService: AdvancedFlowApiService
    ) {}

    // TODO: Avoid dependencies in methods in this services (delete getCustomerReference, getPaymentMethodGuest, getDataMethodPayPal)
    /**
     * @private internally is getting the payment token from localstorage with 'TOKEN' key
     */
    private payByUserType(paymentRequests: PaymentRequest[]): Observable<PaymentResponse[]> {
        return !this.sessionService.isAuthenticated()
            ? this.payAsGuest(paymentRequests[0])
            : this.payAsAuthenticated(paymentRequests);
    }

    private getCustomerReferenceBeforePay(): Observable<string> {
        return of(this.configService.collectCompanyInformationByPayment()).pipe(
            switchMap((customerRefereceByTransaction) => {
                if (!customerRefereceByTransaction) {
                    return this.getCustomerReference().pipe(
                        take(1),
                        switchMap((reference: string | null) => {
                            if (!reference && !this.configService.isCustomerReferenceRequired()) {
                                return of('');
                            }
                            return this.updateCustomerReference(reference || '').pipe(map(() => reference || ''));
                        })
                    );
                }
                return of('');
            })
        );
    }

    private updateCartBeforePay(cart: PaymentCart[]): Observable<PaymentCart[]> {
        return of(this.updateAppliedConvenienceFee(cart)).pipe(
            mergeMap((cart) => {
                return of(this.updateExternalDataCart(cart)).pipe(
                    mergeMap((cart) => {
                        return of(this.updateAmountWithPromoCode(cart));
                    })
                );
            })
        );
    }

    private updateAppliedConvenienceFee(cart: PaymentCart[]): PaymentCart[] {
        cart.forEach((cartElement) => {
            cartElement.isAppliedConvenienceFeeFlag = cartElement.convenienceFee ? true : false;
        });
        return cart;
    }

    private updateExternalDataCart(cart: PaymentCart[]): PaymentCart[] {
        if (this.configService.addFacilityCode()) {
            const facilityCode = this.choiceInformationService.getFacilityCode();

            cart.forEach((cartElement) => {
                if (cartElement.externalData) {
                    cartElement.externalData.facilityCode = facilityCode;
                }
            });
        }

        if (this.configService.addFirmsCodeInExternalData()) {
            const firmsCode = this.summaryService.getFirmsCode();

            cart.forEach((cartElement) => {
                if (cartElement.externalData) {
                    cartElement.externalData.firmsCode = firmsCode;
                }
            });
        }

        return cart;
    }

    private updateAmountWithPromoCode(paymentCart: PaymentCart[]): PaymentCart[] {
        const newCart: PaymentCart[] = [];

        paymentCart.forEach((paymentRequest: PaymentCart) => {
            if (
                !!paymentRequest.nonReservationFee &&
                paymentRequest.paymentType === 'ISC' &&
                paymentRequest.awb &&
                this.promoCodeService.isAwbPaidWithPromoCode(paymentRequest.awb) &&
                !this.isNonReservationFeeAdded(newCart, paymentRequest.awb)
            ) {
                paymentRequest.amount = (paymentRequest?.amount || 0) + paymentRequest.nonReservationFee;
                paymentRequest.promoCodeId = this.promoCodeService.getPromoCodeByAWB(paymentRequest.awb);

                delete paymentRequest['nonReservationFee'];
            }
            newCart.push(paymentRequest);
        });

        return newCart;
    }

    isNonReservationFeeAdded(newCart: PaymentCart[], awb: string): boolean {
        return newCart.some((paymentRequest) => {
            return !!paymentRequest.promoCodeId && paymentRequest.awb === awb;
        });
    }

    setSubmitAccures(submitted: boolean): void {
        this._submitAccures.next(submitted);
    }

    getSubmitAccures(): Observable<boolean> {
        return this._submitAccures.asObservable();
    }

    payCart(): Observable<PaymentResponse[]> {
        return this.getPaymentsRequests$().pipe(
            take(1),
            switchMap((paymentRequests: PaymentRequest[]) => {
                return this.payByUserType(paymentRequests).pipe(take(1));
            }),
            switchMap((payResults: PaymentResponse[]) => {
                const cartResponses = this.getCartResponses(payResults);
                this.paymentConfirmationService.setConfirmationInformation(cartResponses);
                return of(cartResponses);
            })
        );
    }

    getCartResponses(payResults: PaymentResponse[]): PaymentResponse[] {
        return <PaymentResponse[]>payResults.map((payResult: PaymentResponse) => ({
            saleStatus: payResult.saleStatus || SaleStatus.SALE_ERROR,
            paymentMethod: <PaymentMethodsType>payResult.paymentMethod || undefined,
            totalPayments: payResult.totalPayments,
            completedPayments: payResult.completedPayments || [],
            notificationEmail: payResult.notificationEmail,
            transactionId: payResult.transactionId || undefined,
            errorPayment: payResult.errorPayment,
            amount: payResult.amount,
            authenticationToken: payResult.authenticationToken || '',
        }));
    }

    private getPaymentsRequests$(): Observable<PaymentRequest[]> {
        return combineLatest([this.getPaymentRequestBelowThreshold(), this.getPaymentRequestAboveThreshold()]).pipe(
            map(([paymentRequestBelowThreshold, paymentRequestAboveThreshold]) => {
                return <PaymentRequest[]>(
                    [paymentRequestBelowThreshold, paymentRequestAboveThreshold].filter((request) => request !== null)
                );
            })
        );
    }

    private getPaymentRequest(): Observable<PaymentRequest | null> {
        const payments = this.cartBillService.getCartPaymentRequests();
        const isGuest = !this.sessionService.isAuthenticated();

        return forkJoin([
            this.getCustomerReferenceBeforePay().pipe(take(1)),
            this.updateCartBeforePay(payments).pipe(take(1)),
            this.paymentMethodsHandlerService.getPaymentTokenOfPaymentMethodSelected$(false, isGuest).pipe(take(1)),
        ]).pipe(
            map(([customerReference, clientCart, paymentToken]) => {
                if (!paymentToken) {
                    return null;
                }

                const paymentMethod = this.paymentMethodsHandlerService.instant_payment_method_selected?.method;

                return {
                    clientCart,
                    paymentMethod,
                    paymentToken,
                    customerReference,
                } as PaymentRequest;
            })
        );
    }

    private getPaymentRequestBelowThreshold(): Observable<PaymentRequest | null> {
        const payments = this.cartBillService.getCartPaymentRequests(false);
        const isGuest = !this.sessionService.isAuthenticated();

        return of(payments.length).pipe(
            switchMap((length) => {
                if (length) {
                    return forkJoin([
                        this.getCustomerReferenceBeforePay().pipe(take(1)),
                        this.updateCartBeforePay(payments).pipe(take(1)),
                        this.paymentMethodsHandlerService
                            .getPaymentTokenOfPaymentMethodSelected$(false, isGuest)
                            .pipe(take(1)),
                    ]).pipe(
                        map(([customerReference, clientCart, paymentToken]) => {
                            if (!paymentToken) {
                                return null;
                            }
                            const adyenPaymentRequest =
                                this.advancedFlowApiService.getAdyenRegisterCardRequestValue() || undefined;
                            if (isGuest && paymentToken) {
                                return {
                                    clientCart,
                                    paymentToken,
                                    adyenPaymentDataRequest: adyenPaymentRequest,
                                } as PaymentRequest;
                            }
                            const paymentMethod =
                                this.paymentMethodsHandlerService.instant_payment_method_selected?.method;
                            const adyenPaymentDataRequest = this.buildAdyenPaymentRequest(paymentToken);

                            return {
                                clientCart,
                                paymentMethod,
                                paymentToken,
                                variableTerms: true,
                                customerReference,
                                adyenPaymentDataRequest,
                            } as PaymentRequest;
                        })
                    );
                }

                return of(null);
            })
        );
    }

    private getPaymentRequestAboveThreshold(): Observable<PaymentRequest | null> {
        return this.cartBillService.isCartAboveThreshold$().pipe(
            switchMap((isCartAboveThreshold: boolean) => {
                if (!isCartAboveThreshold) {
                    return of(null);
                }
                const payments = this.cartBillService.getCartPaymentRequests(true);

                return forkJoin([
                    this.getCustomerReferenceBeforePay().pipe(take(1)),
                    this.updateCartBeforePay(payments).pipe(take(1)),
                    this.paymentMethodsHandlerService.getPaymentTokenOfPaymentMethodSelected$(true).pipe(take(1)),
                ]).pipe(
                    map(([customerReference, clientCart, paymentToken]) => {
                        const paymentMethod =
                            this.paymentMethodsHandlerService.instant_payment_method_above_threshold?.method;
                        const adyenPaymentDataRequest = this.buildAdyenPaymentRequest(paymentToken);
                        return {
                            clientCart,
                            paymentMethod,
                            paymentToken,
                            variableTerms: true,
                            customerReference,
                            adyenPaymentDataRequest,
                        } as PaymentRequest;
                    })
                );
            })
        );
    }

    updateCustomerReference(customerReference: string): Observable<PaymentCart[]> {
        return this.cartService.updateCustomerReference(customerReference);
    }

    isAllowedToMakeAPayment(): Observable<boolean> {
        const sources = this.getValidations().map((o) => o.pipe(take(1)));
        const joined$ = sources.length ? forkJoin(sources) : of([]);

        return joined$.pipe(
            defaultIfEmpty([]),
            take(1),
            switchMap((validations: PaymentValidation[]) => {
                const allExceptions: CartException[] = validations.reduce((result, validation) => {
                    if (!validation.isValid && validation.exception) {
                        return [...result, validation.exception];
                    }

                    return result;
                }, [] as CartException[]);

                if (allExceptions.length) {
                    return throwError(() => allExceptions);
                }

                return of(true);
            })
        );
    }

    isAllowedToMakeAPayment$(): Observable<boolean> {
        return combineLatest(this.getValidations()).pipe(
            defaultIfEmpty([]),
            map((validations: PaymentValidation[]) => {
                return validations.every((v) => v.isValid);
            })
        );
    }

    getPaymentValidationExceptions$(): Observable<CartException[]> {
        return forkJoin(this.getValidations()).pipe(
            defaultIfEmpty([]),
            map((validations: PaymentValidation[]) => {
                return validations.reduce((result, validation) => {
                    if (!validation.isValid && validation.exception) {
                        return [...result, validation.exception];
                    }
                    return result;
                }, [] as CartException[]);
            })
        );
    }

    private getValidations(): Observable<PaymentValidation>[] {
        const validations = [
            this.isTermsOfUseAcceptedToPay(),
            this.isCustomerReferenceValidToPay(),
            this.isThereDuplicatesToPay$(),
            this.isThereMixesToPay$(),
            this.isPaymentMethodValidToPay(),
            this.isAccountOnHold(),
        ];

        if (this.configService.isCompanyPolicyRequired()) {
            validations.push(this.isCompanyPolicyAcceptedToPay());
        }

        return validations;
    }

    isPaymentMethodValidToPay(): Observable<PaymentValidation> {
        return this.sessionService.isAuthenticated$().pipe(
            switchMap((isAuthenticated) => {
                return this.featureRestrictionsService.isPaymentMethodValid$().pipe(
                    map((isPaymentMethodValid) => {
                        if (!isPaymentMethodValid) {
                            if (!isAuthenticated) {
                                return {
                                    isValid: false,
                                    exception: CartException.fromJson({
                                        code: CartExceptionType.PAYMENT_METHOD_INVALID,
                                    }),
                                };
                            }

                            return {
                                isValid: false,
                                exception: CartException.fromJson({
                                    code: CartExceptionType.PAYMENT_METHOD_INVALID,
                                }),
                            };
                        }

                        return {
                            isValid: true,
                        };
                    })
                );
            })
        );
    }

    isTermsOfUseAcceptedToPay(): Observable<PaymentValidation> {
        return this.summaryService.getTermsOfUseValid$().pipe(
            map((termsOfUseForm: TermsOfUseForm) => termsOfUseForm?.isValidForm),
            map((isTermsOfUseAccepted) => {
                if (!isTermsOfUseAccepted) {
                    return {
                        isValid: false,
                        exception: CartException.fromJson({
                            code: CartExceptionType.TERMS_OF_USE_INVALID,
                        }),
                    };
                }

                return {
                    isValid: true,
                };
            })
        );
    }

    isCustomerReferenceValidToPay(): Observable<PaymentValidation> {
        return of(this.configService.collectCompanyInformationByPayment()).pipe(
            switchMap((customerRefereceByTransaction) => {
                if (customerRefereceByTransaction) {
                    return of({
                        isValid: true,
                    });
                }
                const customerReference = this.dynamicsFieldsService.getCollectorFormValueByKey('customerRef');
                if (!this.configService.isCustomerReferenceRequired() && !customerReference) {
                    return of({
                        isValid: true,
                    });
                }
                return this.informationCollectorByPaymentService.getCollectorForms$().pipe(
                    map((form) => form[0]?.valid),
                    map((isCustomerReferenceValid) => {
                        if (!isCustomerReferenceValid) {
                            return {
                                isValid: false,
                                exception: CartException.fromJson({
                                    code: CartExceptionType.CUSTOMER_REFERENCE_INVALID,
                                }),
                            };
                        }
                        return {
                            isValid: true,
                        };
                    })
                );
            })
        );
    }

    isThereDuplicatesToPay$(): Observable<PaymentValidation> {
        return this.cartBillService.isThereDuplicates$().pipe(
            map((isPaymentsDuplicates) => {
                if (isPaymentsDuplicates) {
                    return {
                        isValid: false,
                        exception: CartException.fromJson({
                            code: CartExceptionType.DUPLICATE_PAYMENT,
                        }),
                    };
                }

                return {
                    isValid: true,
                };
            })
        );
    }

    isThereMixesToPay$(): Observable<PaymentValidation> {
        return this.cartBillService.isThereMixes$().pipe(
            map((isThereMixes) => {
                if (isThereMixes) {
                    return {
                        isValid: false,
                        exception: CartException.fromJson({
                            code: CartExceptionType.MIXES_PAYMENT,
                        }),
                    };
                }

                return {
                    isValid: true,
                };
            })
        );
    }

    isCompanyPolicyAcceptedToPay(): Observable<PaymentValidation> {
        return this.summaryService.getNotesValid$().pipe(
            map((notesForm: NotesForm) => notesForm?.isValidForm),
            map((isCompanyPolicyAccepted) => {
                if (!isCompanyPolicyAccepted) {
                    return {
                        isValid: false,
                        exception: CartException.fromJson({
                            code: CartExceptionType.COMPANY_POLICY,
                        }),
                    };
                }

                return {
                    isValid: true,
                };
            })
        );
    }

    isAccountOnHold(): Observable<PaymentValidation> {
        return this.featureRestrictionsService.isAccountOnHold().pipe(
            map((isAccountOnHold) => {
                if (isAccountOnHold) {
                    return {
                        isValid: false,
                        exception: CartException.fromJson({
                            code: CartExceptionType.ACCOUNT_ON_HOLD,
                        }),
                    };
                }

                return {
                    isValid: true,
                };
            })
        );
    }

    getCustomerReference(): Observable<string | null> {
        return of(this.dynamicsFieldsService.getCollectorFormValueByKey('customerRef'));
    }

    payAsAuthenticated(paymentRequests: PaymentRequest[]): Observable<PaymentResponse[]> {
        if (paymentRequests[0].paymentMethod !== PaymentMethods.PAYPAL) {
            return this.makePayment(paymentRequests);
        }

        return this.paypalService.getDataMethodPayPal().pipe(
            switchMap((payload: PayloadPayPal | null) => {
                if (!payload) {
                    return throwError(() =>
                        CartException.fromJson({
                            code: CartExceptionType.PAYMENT_METHOD_INVALID,
                        })
                    );
                }

                const payPalPaymentDataRequest: PayPalPaymentDataRequest | undefined = ({} =
                    this.buildPaypalDataRequest(payload));

                return this.makePayment([
                    {
                        ...paymentRequests[0],
                        payPalPaymentDataRequest,
                    },
                ]);
            })
        );
    }

    payAsGuest({
        clientCart,
        customerReference,
        paymentToken,
        adyenPaymentDataRequest,
    }: PaymentRequest): Observable<PaymentResponse[]> {
        return this.paymentMethodsHandlerService.getPaymentMethodGuest().pipe(
            take(1),
            switchMap((paymentMethodGuest: PaymentMethodGuest | null) => {
                if (paymentMethodGuest) {
                    if (paymentMethodGuest.paymentMethod === PaymentMethods.PAYPAL) {
                        return this.paypalService.getDataMethodPayPal().pipe(
                            switchMap((payload: PayloadPayPal | null) => {
                                const paymentRequest = this.buildGuestPaypalRequest(
                                    paymentMethodGuest.paymentMethod || PaymentMethods.PAYPAL,
                                    payload!,
                                    clientCart,
                                    customerReference,
                                    paymentToken
                                );
                                return this.makePayment([paymentRequest]);
                            })
                        );
                    } else if (paymentMethodGuest.paymentMethod === PaymentMethods.ECHECK) {
                        let addressForm: AddressFormType | undefined;
                        let personalForm: PersonalFormType | undefined;

                        if (paymentMethodGuest?.addressFormGroup) {
                            addressForm = paymentMethodGuest?.addressFormGroup?.getRawValue();
                        }

                        if (paymentMethodGuest?.personalFormGroup) {
                            personalForm = paymentMethodGuest?.personalFormGroup?.getRawValue();
                        }

                        const paymentRequest = this.buildGuestEcheckPaymentRequest(
                            paymentMethodGuest.echeckOTToken || '',
                            clientCart,
                            customerReference,
                            addressForm,
                            personalForm,
                            paymentMethodGuest.paymentProcessor
                        );

                        return this.makePayment([paymentRequest]);
                    } else {
                        if (paymentMethodGuest.creditCardNonce || paymentMethodGuest.paymentVehicle) {
                            let creditCardForm: CreditCardForm | undefined;
                            let addressForm: AddressFormType | undefined;
                            if (paymentMethodGuest.creditCardForm) {
                                creditCardForm = paymentMethodGuest.creditCardForm;
                                if (paymentMethodGuest?.addressFormGroup) {
                                    addressForm = paymentMethodGuest?.addressFormGroup?.getRawValue();
                                }
                            }

                            const paymentRequest = this.buildGuestPaymentRequest(
                                creditCardForm,
                                clientCart,
                                customerReference,
                                addressForm,
                                paymentMethodGuest.creditCardNonce,
                                adyenPaymentDataRequest
                            );

                            return this.makePayment([paymentRequest]);
                        }
                    }
                }

                return throwError(() =>
                    CartException.fromJson({
                        code: CartExceptionType.PAYMENT_METHOD_INVALID,
                    })
                );
            })
        );
    }

    goToConfirmation(): void {
        this.router.navigate(['cart/confirmation']);
    }

    makePayment(paymentRequests: PaymentRequest[]): Observable<PaymentResponse[]> {
        const isCartAboveThreshold = this.cartBillService.isCartAboveThreshold();
        return this.paymentService
            .pay(paymentRequests)
            .pipe(
                switchMap((transactionResults: PaymentResponse[]) =>
                    this.handlePaymentResults(transactionResults, isCartAboveThreshold)
                )
            );
    }

    public handlePaymentResults(
        transactionResults: PaymentResponse[],
        isCartAboveThreshold: boolean
    ): Observable<PaymentResponse[]> {
        const successfulPayments = transactionResults.find(
            (payResult: PaymentResponse) => payResult.saleStatus === SaleStatus.SALE_SUCCESS
        );

        if (successfulPayments) {
            this.paymentConfirmationService.setWasCartAboveThreshold(isCartAboveThreshold);
            this.paymentCompleted(SaleStatus.SALE_SUCCESS);

            return this.cartHandlerService.getCartRequest$().pipe(
                take(1),
                map(() => transactionResults),
                catchError(() => {
                    return throwError(() =>
                        CartException.fromJson({
                            code: CartExceptionType.UNKNOWN,
                            description: `Something went wrong, please contact support@cargosprint.com`,
                        })
                    );
                })
            );
        }

        this.paymentCompleted(SaleStatus.SALE_ERROR);

        const errorPayment = transactionResults.find(
            (payResult: PaymentResponse) => payResult.saleStatus === SaleStatus.SALE_ERROR
        );

        return throwError(() => errorPayment?.errorPayment?.error);
    }

    paymentCompleted(status: SaleStatus): void {
        if (status === SaleStatus.SALE_SUCCESS) {
            this.cartBillService.newEvent(CartEvents.paymentSuccess);
            this.summaryService.newCurrentPaymentStatusEvent(PaymentStatusType.PAYMENT_SUCESS);
        } else {
            this.summaryService.newCurrentPaymentStatusEvent(PaymentStatusType.PAYMENT_ERROR);
        }
    }

    buildGuestPaypalRequest(
        paymentMethod: PaymentMethodsType,
        payLoad: PayloadPayPal,
        clientCart: PaymentCart[],
        customerReference: string,
        paymentToken?: PaymentToken
    ): PaymentRequest {
        const payPalPaymentDataRequest = this.buildPaypalDataRequest(payLoad);
        return PaymentRequest.fromJson({
            paymentToken,
            clientCart,
            paymentMethod,
            customerReference: customerReference,
            paymentMetaData: {
                firstName: payLoad.details?.firstName,
                lastName: payLoad.details?.lastName,
                email: payLoad.details?.email,
                city: payLoad.details?.shippingAddress?.city,
                state: payLoad.details?.shippingAddress?.state,
                postalCode: payLoad.details?.shippingAddress?.postalCode,
                country: payLoad.details?.shippingAddress?.countryCode,
                street: payLoad.details?.shippingAddress?.line1,
                customerReference,
            },
            payPalPaymentDataRequest,
        });
    }

    buildGuestPaymentRequest(
        creditCardForm: CreditCardForm | undefined,
        clientCart: PaymentCart[],
        customerReference: string,
        addressInformation?: AddressFormType,
        nonceResponse?: TokenizedCardPayload,
        adyenPaymentDataRequest?: AdyenRegisterCardRequest
    ): PaymentRequest {
        return PaymentRequest.fromJson({
            paymentMetaData: {
                ...creditCardForm,
                city: addressInformation?.city,
                state: addressInformation?.state,
                postalCode: addressInformation?.zipCode,
                country: addressInformation?.country,
                street: addressInformation?.street,
                street2: addressInformation?.street2,
                customerReference,
            },
            customerReference,
            paymentNonce: nonceResponse?.nonce,
            paymentToken: this.getPaymentTokenForGuestUserCreditCards(
                nonceResponse?.details.expirationMonth || '',
                nonceResponse?.details.expirationYear || '',
                nonceResponse?.details.lastFour || '',
                nonceResponse?.details.cardType || ''
            ),
            clientCart,
            paymentMethod: PaymentMethods.CREDIT_CARD,
            adyenPaymentDataRequest,
        });
    }

    buildGuestEcheckPaymentRequest(
        token: string,
        clientCart: PaymentCart[],
        customerReference: string,
        addressInformation?: AddressFormType,
        personalInformation?: PersonalFormType,
        paymentProcessor?: string
    ): PaymentRequest {
        return PaymentRequest.fromJson({
            paymentMetaData: {
                ...personalInformation,
                city: addressInformation?.city,
                state: addressInformation?.state,
                postalCode: addressInformation?.zipCode,
                country: addressInformation?.country,
                street: addressInformation?.street,
                street2: addressInformation?.street2,
                customerReference,
            },
            customerReference,
            paymentToken: this.getPaymentTokenForGuestUserEcheck(
                token,
                personalInformation?.email || '',
                paymentProcessor
            ),
            clientCart,
            paymentMethod: PaymentMethods.ECHECK,
        });
    }

    buildPaypalDataRequest(response: PayloadPayPal): PayPalPaymentDataRequest {
        let totalAmount: number = this.cartBillService.getTotalCart();
        const phoneNumber = this.customerService.getCustomer().phone;
        return {
            accountType: 'checkout',
            amount: totalAmount,
            email: response.details?.email || '',
            firstName: response.details?.firstName || '',
            fullName: response.details?.shippingAddress?.recipientName || '',
            lastName: response.details?.lastName || '',
            payerId: response.details?.payerId || '',
            paymentNonce: response.nonce || '',
            phoneNumber: phoneNumber,
        };
    }

    getPaymentTokenForGuestUserCreditCards(
        paymentMethodExpirationMonth: string,
        paymentMethodExpirationYear: string,
        paymentMethodNumber: string,
        paymentInstitution: string
    ): PaymentToken {
        return {
            paymentMethod: {
                name: ComponentPaymentMethodName.CREDIT_CARD,
                logTime: 10,
                componentName: PaymentMethods.CREDIT_CARD,
            },
            paymentProcessor: {
                name: ProcessorName.Adyen,
            },
            token: null,
            paymentMethodNumber,
            paymentInstitution,
            paymentMethodExpirationMonth,
            paymentMethodExpirationYear,
        };
    }

    getPaymentTokenForGuestUserEcheck(token: string, email: string, paymentProcessor?: string): PaymentToken {
        const processor: ProcessorName = paymentProcessor === 'REGIONS' ? ProcessorName.Regions : ProcessorName.Forte;
        const paymentToken: PaymentToken = {
            paymentMethod: {
                name: ComponentPaymentMethodName.ECHECK,
                logTime: 10,
                componentName: PaymentMethods.ECHECK,
            },
            paymentProcessor: {
                name: processor,
            },
            token,
            paymentMetaData: {
                email,
            },
        };
        return paymentToken;
    }

    private buildAdyenPaymentRequest(paymentToken: PaymentToken | null): AdyenRegisterCardRequest | null {
        if (!paymentToken) {
            return null;
        }
        if (paymentToken.paymentProcessor?.name !== ProcessorName.Adyen || !paymentToken.buyerIPV4Address) {
            return null;
        }
        return {
            BuyerIPV4Address: paymentToken.buyerIPV4Address,
        };
    }
}
